import { formatDate, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import * as moment from 'moment';
import { GroupService } from './group.service';
import { UserService } from './user.service';
import { FileDownloadService } from './file-download.service';
import { FormatMessagePipe } from 'src/app/shared/pipes/format-message.pipe';

import { environment } from './../../../environments/environment';
import {
  ALL_CHROME, FOOTER, LETTERHEAD, MARGIN, NO_CHROME, PageDims, contentBottom, contentTop,
  coverPagePredicate, fitFullPage, pageDimsForImage, textWrapWidth,
} from './pdf-layout';
/**
 * Everything a PDF needs, as one object.
 *
 * Replaces nine positional parameters. Three call sites already read
 * `…, false, undefined, undefined, date)`, which is the shape that makes adding a tenth dangerous.
 */
export interface PdfBuildRequest {
  content: string;
  filename: string;
  patient: any;
  images?: any[];
  attachSignature?: boolean;
  signatureUrl?: string;
  signatoryName?: string;
  date?: string | Date;
  embedMediaAsGrid?: boolean;

  /**
   * §A of `tasks/pdf-letterhead-placement.md` — a cover page, then bare full-page images.
   *
   * When present, `images` is ignored: `coverImages` takes its place on the cover, and
   * `fullPageImages` become one unchromed page each after it.
   */
  coverPage?: CoverPageRequest;
}

/**
 * The returned-document shape.
 *
 * `coverImages` are the CLINICIAN's attachments — they belong on the cover, beside the observations
 * they illustrate. `fullPageImages` are the PATIENT's returned pages, which are the document itself
 * and get a page each at full size, with no letterhead, no footer and no page number: a full-bleed
 * clinical scan must not be overprinted with anything.
 */
export interface CoverPageRequest {
  coverImages?: any[];
  fullPageImages: any[];

  /**
   * THE DOCUMENT ON ITS OWN - no cover, no letterhead, no observations, no page numbers.
   *
   * The timeline's export menu offers this as "Include cover page", and unticking it means the
   * clinician wants the pages the patient sent back and nothing else - typically because those
   * pages are about to be forwarded to a third party who has no business reading the practice's
   * internal commentary on them.
   *
   * It DROPS the cover rather than blanking it: the empty first page jsPDF opens with is deleted
   * once the returned pages are in, so page 1 of the file is page 1 of the document.
   *
   * A request that omits the cover and has NO page to put in its place THROWS rather than
   * producing an empty PDF. Both callers already treat a render failure as "send nothing" - a
   * blank file reads as a document that was lost, which is the worse outcome.
   */
  omitCover?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PdfDownloadService {
  group!: any;
  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private userService: UserService,
    private groupService: GroupService,
    private fileDownloadService: FileDownloadService
  ) {

  }

  /** jsPDF expects short names like JPEG / PNG / WEBP */
  private mimeToJsPdfFormat(mime: string): string {
    const m = mime.toLowerCase();
    if (m.includes('jpeg') || m.includes('jpg')) return 'JPEG';
    if (m.includes('png')) return 'PNG';
    if (m.includes('webp')) return 'WEBP';
    return 'JPEG';
  }

  private parseDataUrlMime(dataUrl: string): string {
    const m = dataUrl.match(/^data:([^;]+);/);
    return m?.[1] || 'image/jpeg';
  }

  /**
   * WHAT A PDF IS MADE OF — `tasks/pdf-letterhead-placement.md` §2.2.
   *
   * `downloadPdf` and `generatePdfAsBase64` used to be ~160 near-identical lines EACH, and neither
   * called the other. Every change had to be made twice, and four of them had already drifted apart
   * (see `buildPdf` for which, and which way each was resolved).
   *
   * An options object rather than the nine positional parameters they carried: three call sites
   * already ended `…, false, undefined, undefined, date)`, and that shape is what makes the next
   * parameter dangerous to add.
   */
  async downloadPdf(req: PdfBuildRequest): Promise<void> {
    const doc = await this.buildPdf(req);
    if (!doc) return;

    // Use our platform-aware download service instead of jsPDF's own doc.save(),
    // which internally uses <a download> and is ignored by iOS WKWebView.
    const blob = doc.output('blob') as Blob;
    await this.fileDownloadService.download(blob, `${req.filename}.pdf`);
  }

  /**
   * The same document, as a base64 data URL — for WhatsApp, and for anything that has to hand the
   * bytes to the server rather than to the user.
   */
  async generatePdfAsBase64(req: PdfBuildRequest): Promise<string> {
    const doc = await this.buildPdf(req);
    if (!doc) return '';
    return doc.output('datauristring');
  }

  /**
   * Build the document. The ONE implementation; the two public methods differ only in what they do
   * with the finished `doc`.
   *
   * ── THE FOUR DIVERGENCES THIS RESOLVES ─────────────────────────────────────────────────
   *
   * 1. **A signature image that fails to load REJECTS**, as `downloadPdf` did. The base64 path used
   *    to swallow it and produce a silently UNSIGNED clinical document, which is the worse of the
   *    two failures by a distance — a missing PDF is noticed, an unsigned one is filed.
   * 2. The signature is drawn only when there is a `signatureUrl` to draw, as the base64 path
   *    guarded. `downloadPdf` would otherwise reach `doc.text(undefined)` for a nameless signatory.
   * 3. `signatureUrl` is tested for truthiness, which is what both were doing by different spellings.
   * 4. A footer image that fails to load rejects. Unchanged, both did.
   *
   * ── WHAT HAS DELIBERATELY NOT CHANGED ───────────────────────────────────────────────
   *
   * `contentTop`/`contentBottom` are read through `ALL_CHROME`, which returns exactly the 55 and
   * `height - 55` the old literals used — that equality is the first assertion in
   * `pdf-layout.spec.ts`, and it is what makes the unification provably output-identical.
   *
   * The text wrap was output-identical here too, and was then corrected SEPARATELY in §1.5 (see
   * `textWrapWidth` below), because that one change re-flows every PDF this product has made.
   */
  private async buildPdf(req: PdfBuildRequest): Promise<any | null> {
    if (!isPlatformBrowser(this.platformId)) return null;

    const [jsPDFModule] = await Promise.all([
      import('jspdf'),
      // `jspdf-autotable` registers itself onto jsPDF as a side effect. Nothing in this service
      // calls `autoTable`, but the import is kept: it is load-bearing for anything that later
      // does, and removing it is not this change's business.
      import('jspdf-autotable'),
    ]);

    const { content, filename, patient, images, attachSignature, signatureUrl, signatoryName,
      date, embedMediaAsGrid, coverPage } = req;

    this.group = this.groupService.group;

    // Strip HTML tags to convert to plain text
    const plainTextContent = this.convertToPlainText(content);

    const { jsPDF } = jsPDFModule;
    const user = this.userService.user;

    const currentDateTime = formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss', 'en-US');

    const doc = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    const dims: PageDims = {
      width: doc.internal.pageSize.getWidth(),
      height: doc.internal.pageSize.getHeight(),
    };

    /*
     * THIS USED TO BE `getWidth() + 30` — 240mm on a 210mm page.
     *
     * Wrapping was therefore effectively disabled: `splitTextToSize` only ever broke on explicit
     * newlines, and any paragraph longer than one line ran off the right edge and was clipped by
     * the renderer. Nothing errored; the text simply was not on the page. The clinician's
     * observations on a returned-document cover are exactly the free-form prose this loses, which
     * is why it is fixed now rather than left (`tasks/pdf-letterhead-placement.md` §1.5).
     *
     * It re-flows every PDF this product has ever produced, so it lands on its own.
     */
    // Measure at the body size the lines are drawn in. jsPDF starts at 16pt, and lines measured at
    // 16pt but drawn at 11pt only fill about two thirds of the width.
    doc.setFontSize(11);
    const lines = doc.splitTextToSize(plainTextContent, textWrapWidth(dims));

    // Load the logo, signature and footer images
    const logoImage = await this.getBase64ImageFromURL(this.group.headerImageUrl);
    const signatureImage = signatureUrl ? await this.getBase64ImageFromURL(signatureUrl) : null;
    const footerImage = this.group.footerImageUrl
      ? await this.getBase64ImageFromURL(this.group.footerImageUrl)
      : null;

    // Set up the header with the logo image or practice letterhead
    const headerFunction = () => {
      return new Promise<void>((resolve, reject) => {
        const logoHeight = LETTERHEAD.imageHeight;
        const thisPageWidth = doc.internal.pageSize.getWidth();
        const pageCenter = thisPageWidth / 2;

        const img = new Image();
        img.onload = () => {
          const aspectRatio = img.width / img.height;
          const logoWidth = logoHeight * aspectRatio;

          const logoX = pageCenter - logoWidth / 2;
          const logoY = LETTERHEAD.imageY;
          doc.addImage(img, this.mimeToJsPdfFormat(this.parseDataUrlMime(logoImage)),
            logoX, logoY, logoWidth, logoHeight);

          doc.setFontSize(16);
          doc.setTextColor('#353740');

          resolve();
        };
        img.onerror = () => reject(new Error('Failed to load header logo for PDF'));
        img.src = logoImage;
      });
    };

    const signatureFunction = (y: number) => {
      //Adding signature to the pdf
      return new Promise<number>((resolve, reject) => {
        if (!signatureUrl || !signatureImage) {
          resolve(0);
          return;
        }

        const imgHeight = 20;
        const img = new Image();
        img.onload = () => {
          const aspectRatio = img.width / img.height;
          const imgWidth = imgHeight * aspectRatio;

          const posX = 10;
          const posY = y;
          doc.addImage(img, this.mimeToJsPdfFormat(this.parseDataUrlMime(signatureImage)),
            posX, posY, imgWidth, imgHeight);
          resolve(Math.abs(imgHeight + 5));
        };

        // REJECTS — see the header. A silently unsigned clinical document is the worse failure.
        img.onerror = () => reject(new Error('Failed to load signature image for PDF'));
        img.src = signatureImage;
      });
    };

    /**
     * The footer, in two parts.
     *
     * `drawBrand` is the practice's footer image plus the "Generated by…" line; `drawPageNumber` is
     * "Page N of M". They are separate because Phase B's "footer on the last page only" moves the
     * FIRST and never the second — page numbers are how a reader knows a printed document is
     * complete (task doc §0.6). Today both are drawn together on every chromed page, which is
     * exactly what the old single function did.
     */
    const footerFunction = (pageNumber: number, totalPages: number) => {
      return new Promise<void>((resolve, reject) => {
        const drawText = (baseline: number) => {
          doc.setFontSize(8);
          doc.text(
            `Generated by ${user.title} ${user.name} ${user.surname} using MedicAI (contact@medicai.co.za) on ${currentDateTime}`,
            10, doc.internal.pageSize.getHeight() - baseline);
          doc.text(`Page ${pageNumber} of ${totalPages}`,
            doc.internal.pageSize.getWidth() - 10, doc.internal.pageSize.getHeight() - baseline,
            { align: 'right' });
        };

        if (footerImage) {
          const footerImageHeight = FOOTER.imageHeight;
          const thisPageWidth = doc.internal.pageSize.getWidth();
          const pageCenter = thisPageWidth / 2;

          const img = new Image();
          img.onload = () => {
            const aspectRatio = img.width / img.height;
            const footerImageWidth = footerImageHeight * aspectRatio;

            const imgX = pageCenter - footerImageWidth / 2;
            const imgY = doc.internal.pageSize.getHeight() - FOOTER.imageTopOffset;
            doc.addImage(img, this.mimeToJsPdfFormat(this.parseDataUrlMime(footerImage)),
              imgX, imgY, footerImageWidth, footerImageHeight);

            drawText(FOOTER.textBaseline);
            resolve();
          };
          img.onerror = () => reject(new Error('Failed to load footer image for PDF'));
          img.src = footerImage;
        } else {
          drawText(FOOTER.textOnlyBaseline);
          resolve();
        }
      });
    };

    /*
     * The page geometry, read through the predicate rather than written as literals.
     *
     * `ALL_CHROME` makes `contentTop` return 55 and `contentBottom` return `height - 55` — the
     * exact numbers the old code had at four and two sites. Cover-page mode swaps the predicate
     * and nothing else here changes.
     */
    const chromeOf = ALL_CHROME;
    const topOfPage = contentTop(chromeOf(1, 1));

    let y = topOfPage;

    /*
     * THE COVER IS THE WHOLE OF THE BLOCK BELOW, and `omitCover` skips all of it.
     *
     * The demographics, the observations, the signature and the clinician's attachments are one
     * thing - the practice's covering note - and dropping "the cover page" means dropping them
     * together. Leaving any one of them behind would produce a stray letterheaded page in front
     * of a document that was asked for without one.
     */
    const omitCover = !!(coverPage && coverPage.omitCover);

    if (!omitCover) {
      doc.setFontSize(11);

      doc.text(date ? moment(date).format('DD/MM/YYYY') : moment().format('DD/MM/YYYY'),
        MARGIN.left, y);
      y += 5;
      doc.text(`Name: ${patient.name} ${patient.surname}`, MARGIN.left, y);
      y += 5;
      doc.text(`DOB: ${typeof patient.dob === 'string' ? patient.dob : moment(patient.dob).format('DD/MM/YYYY')}`, MARGIN.left, y);
      y += 10;

      for (const line of lines) {
        if (y > contentBottom(chromeOf(1, 1), dims)) {
          doc.addPage();
          y = topOfPage;
        }

        doc.text(line, MARGIN.left, y); // Adjust the x-coordinate for left margin
        y += doc.getLineHeightFactor() * 5;
      }

      if (attachSignature && signatureUrl) {
        y += await signatureFunction(y);
        doc.setFontSize(11);
        doc.text(signatoryName as string, MARGIN.left, y);
      }
    }

    /*
     * ── THE MEDIA, AND THE PREDICATE THAT DECIDES WHAT IS CHROMED ────────────────────────
     *
     * Ordinary documents keep `ALL_CHROME` and flow their media exactly as before.
     *
     * A COVER-PAGE document instead flows the clinician's attachments ONTO the cover (no leading
     * page break), counts how many pages the cover ended up being, and only then decides which
     * pages carry chrome. The count has to be taken here and not assumed to be 1: attachments can
     * overflow, and an overflow page is a cover-continuation page that legitimately keeps its
     * letterhead.
     */
    let chromePredicate = chromeOf;

    if (omitCover) {
      /*
       * The pages, and then the blank page they were added AFTER.
       *
       * `addFullPageImage` always calls `addPage`, because in cover mode there is always a cover
       * in front of it. With no cover, jsPDF's own page 1 is left over and has to go - and it can
       * only go once something else is in the document, since a zero-page jsPDF throws on save.
       */
      for (const image of (coverPage!.fullPageImages || [])) {
        await this.addFullPageImage(doc, image);
      }
      if (doc.getNumberOfPages() < 2) {
        // Nothing embedded: every page failed to load, or there were none to begin with. See
        // `omitCover` - a blank PDF reads as a lost document, so this is a failure, not a file.
        throw new Error('The returned document has no pages that can be exported without a cover');
      }
      doc.deletePage(1);
      chromePredicate = NO_CHROME;
    } else if (coverPage) {
      if (coverPage.coverImages && coverPage.coverImages.length > 0) {
        await this.embedTimelineImagesInJsPdf(
          doc, coverPage.coverImages, embedMediaAsGrid === true,
          // `y` is where the observations (and the signature, if any) finished. A little air
          // under the last line so an image does not touch the text it illustrates.
          { startOnNewPage: false, startY: y + 6 });
      }

      // AFTER the cover has finished growing — see above.
      chromePredicate = coverPagePredicate(doc.getNumberOfPages());

      for (const image of coverPage.fullPageImages || []) {
        await this.addFullPageImage(doc, image);
      }
    } else if (images && images.length > 0) {
      await this.embedTimelineImagesInJsPdf(doc, images, embedMediaAsGrid === true);
    }

    // Apply the header and footer to each page
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      const chrome = chromePredicate(i, totalPages);
      if (chrome.header) await headerFunction();
      if (chrome.footer) await footerFunction(i, totalPages);
    }

    return doc;
  }

  /**
   * One returned page, filling one page of its own.
   *
   * CONTAIN, never crop (`fitFullPage`): a returned document is the patient's completed clinical
   * paperwork, and cropping to fill the page would lose whatever sits at the edge — a signature, a
   * date, a page number they wrote — and lose it invisibly.
   *
   * A markedly landscape scan gets a landscape PAGE rather than being rotated into a portrait one.
   * jsPDF's `addImage` rotation argument pivots on the placement-box origin, behaves differently
   * across versions, and puts the page off-canvas with no error when it is wrong.
   *
   * A page that cannot be loaded is SKIPPED rather than left blank: an empty sheet in the middle of
   * a returned document reads as a page the patient failed to send.
   */
  private async addFullPageImage(doc: any, image: any): Promise<void> {
    const url = image?.mediaUrl;
    if (!url) return;

    let dataUrl: string;
    try {
      dataUrl = await this.getBase64ImageFromURL(url, { pdfEmbed: true });
    } catch {
      return;
    }
    if (!dataUrl) return;

    const loaded = await new Promise<HTMLImageElement | null>((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    });
    if (!loaded) return;

    const dims = pageDimsForImage({ width: loaded.width, height: loaded.height });
    doc.addPage([dims.width, dims.height], dims.width > dims.height ? 'landscape' : 'portrait');

    const placed = fitFullPage({ width: loaded.width, height: loaded.height }, dims);
    doc.addImage(loaded, this.mimeToJsPdfFormat(this.parseDataUrlMime(dataUrl)),
      placed.x, placed.y, placed.width, placed.height);
  }


  /**
   * Embeds timeline/note images: single column (default) or 2x2 grid (up to four images per page).
   * jsPDF uses top-left origin; y increases downward.
   */
  private async embedTimelineImagesInJsPdf(
    doc: any, images: any[], embedMediaAsGrid: boolean,
    options: { startOnNewPage?: boolean; startY?: number } = {},
  ): Promise<void> {
    const sorted = [...images].sort((a, b) => (a.displayIndex ?? 0) - (b.displayIndex ?? 0));
    /*
     * Media has always started on a fresh page. A COVER PAGE is the one case that does not want
     * that: the clinician's attachments illustrate the observations directly above them, and a
     * page break between the two separates a photograph from the sentence about it.
     *
     * Default unchanged, so every existing caller behaves exactly as before.
     */
    if (options.startOnNewPage !== false) doc.addPage();
    const headerHeight = 50;
    const footerHeight = 50;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const gap = 4;

    if (!embedMediaAsGrid) {
      /*
       * STARTS WHERE THE TEXT STOPPED, not at the top of the page.
       *
       * `headerHeight` is right when this began on a fresh page, which it always did until the
       * cover page arrived. Continuing ON the cover and still starting at 50 drew the clinician's
       * attachments straight over the patient demographics and the observations — reported from a
       * real export.
       */
      let y = options.startY ?? headerHeight;
      const maxImageHeight = pageHeight - headerHeight - footerHeight;
      const maxImageWidth = pageWidth - 20;

      for (const image of sorted) {
        const imageDataUrl = await this.getBase64ImageFromURL(image.mediaUrl, { pdfEmbed: true });
        const embeddedMime = this.parseDataUrlMime(imageDataUrl);

        await new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            let imgWidth = maxImageWidth;
            let imgHeight = (img.height / img.width) * imgWidth;
            while (imgHeight > maxImageHeight) {
              imgWidth *= 0.98;
              imgHeight = (img.height / img.width) * imgWidth;
            }
            const totalHeight = y + imgHeight + footerHeight;
            if (totalHeight > pageHeight) {
              doc.addPage();
              y = headerHeight;
            }
            const imgX = (pageWidth - imgWidth) / 2;
            doc.addImage(img, this.mimeToJsPdfFormat(embeddedMime), imgX, y, imgWidth, imgHeight);
            y += imgHeight + 10;
            resolve();
          };
          img.onerror = () => reject(new Error('Failed to decode timeline image for PDF (unsupported or corrupt image)'));
          img.src = imageDataUrl;
        });
      }
      return;
    }

    /*
     * 2x2 grid: partial last page places images in slots 0..n-1 (top-left first).
     *
     * A grid CONTINUING on the cover page has to start below the text, and a grid squeezed into
     * whatever is left of a page is worse than one drawn properly on the next — four cells in
     * 40mm are unreadable. So the cover's text is honoured, and if what remains cannot hold a
     * sensible grid the grid takes a page of its own.
     *
     * HALF THE CONTENT HEIGHT is the threshold: below that a 2x2 grid's cells are shorter than
     * they are wide by more than a factor of two, which is the point at which a portrait
     * photograph stops being legible.
     */
    let contentTop = headerHeight;
    if (options.startY != null) {
      const usable = (pageHeight - footerHeight) - options.startY;
      if (usable >= (pageHeight - headerHeight - footerHeight) / 2) {
        contentTop = options.startY;
      } else {
        doc.addPage();
      }
    }
    const contentBottom = pageHeight - footerHeight;
    const contentHeight = contentBottom - contentTop;
    const contentWidth = pageWidth - 2 * margin;
    const cellW = (contentWidth - gap) / 2;
    const cellH = (contentHeight - gap) / 2;

    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && i % 4 === 0) {
        doc.addPage();
      }
      const slot = i % 4;
      const col = slot % 2;
      const row = Math.floor(slot / 2);
      const cellLeft = margin + col * (cellW + gap);
      const cellTop = contentTop + row * (cellH + gap);

      const image = sorted[i];
      const imageDataUrl = await this.getBase64ImageFromURL(image.mediaUrl, { pdfEmbed: true });
      const embeddedMime = this.parseDataUrlMime(imageDataUrl);

      await new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const nw = img.naturalWidth || img.width;
          const nh = img.naturalHeight || img.height;
          const scale = Math.min(cellW / nw, cellH / nh);
          const drawW = nw * scale;
          const drawH = nh * scale;
          const imgX = cellLeft + (cellW - drawW) / 2;
          const imgY = cellTop + (cellH - drawH) / 2;
          doc.addImage(img, this.mimeToJsPdfFormat(embeddedMime), imgX, imgY, drawW, drawH);
          resolve();
        };
        img.onerror = () => reject(new Error('Failed to decode timeline image for PDF (unsupported or corrupt image)'));
        img.src = imageDataUrl;
      });
    }
  }

  convertToPlainText(content: string): string {

    // Apply the Markdown pipe to format the content
    const formatMessagePipe = new FormatMessagePipe();
    const formattedContent = formatMessagePipe.transform(content);

    // Create a temporary DOM element
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = formattedContent;

    // Convert the DOM to plain text with formatting
    const processNode = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        // Return text nodes as is
        return node.textContent?.length === 1 ? node.textContent.trim() : node.textContent || '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        switch (element.tagName.toLowerCase()) {
          case 'h3':
          case 'p':
            // Add a double newline for paragraphs and headings
            return `\n\n${Array.from(element.childNodes).map(processNode).join('')}`;
          case 'ul':
          case 'br':
            // Add a newline before lists and line breaks
            return `\n${Array.from(element.childNodes).map(processNode).join('')}`;
          case 'li':
            // Add a bullet point for list items
            return `\n• ${Array.from(element.childNodes).map(processNode).join('')}`;
          default:
            // Recursively process other nodes
            return Array.from(element.childNodes).map(processNode).join('');
        }
      }
      return '';
    };

    // Start processing the content
    return processNode(tempDiv).trim();
  }

  /**
   * Returns a full data URL suitable for `Image#src`. For proxied blob URLs, MIME matches Sharp output
   * (not necessarily the stored `mediaType` on the attachment).
   * @param options.pdfEmbed Use server JPEG compression + smaller max width for timeline PDF attachments only.
   */
  private async getBase64ImageFromURL(url: string | undefined | null, options?: { pdfEmbed?: boolean }): Promise<string> {
    if (!url) {
      const assetUrl = 'assets/full-logo.jpg';
      const response = await fetch(assetUrl);
      if (!response.ok) {
        throw new Error(`Failed to load default logo (${response.status})`);
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read logo asset'));
        reader.readAsDataURL(blob);
      });
    }
    const params = new URLSearchParams();
    params.set('url', url);
    if (options?.pdfEmbed) {
      params.set('maxWidth', '800');
      params.set('output', 'jpeg');
      params.set('quality', '80');
    }
    const proxyUrl = `${environment.API_URL}/api/image-proxy?${params.toString()}`;
    const response = await fetch(proxyUrl);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error((data as { error?: string }).error || `Image proxy failed (${response.status})`);
    }
    if (!(data as { base64Image?: string }).base64Image) {
      throw new Error('No image data from proxy');
    }
    const base64Image = (data as { base64Image: string }).base64Image;
    const mimeType = (data as { mimeType?: string }).mimeType || 'image/jpeg';
    return `data:${mimeType};base64,${base64Image}`;
  }
}